import { Form, Formik, useFormikContext } from "formik";
import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { ReactComponent as RightArrow } from "../../../assets/icons/right-arrow.svg";
import Button from "../../../components/Button";
import { Checkbox } from "../../../components/Checkbox";
import FormField from "../../../components/FormField";
import { ShowToast } from "../../../components/toast";
import { api } from "../../../helpers/api";
import { pickAutoSwitchTarget } from "../../../helpers/hooks/use-property-switch";
import {
  getDefaultRouteForUser,
  isValidUserRole,
} from "../../../helpers/permissions";
import { getUser, setToken, setUser } from "../../../helpers/utils";
import type { AuthResponse } from "../../../types/linked-accounts";
import { AuthSplitLayout } from "../components/AuthSplitLayout";

const validationSchema = Yup.object().shape({
  email: Yup.string()
    // .matches(/^[^\s+@]+@[^\s@]+\.[^\s@]+$/, "Invalid email address")
    .email("Invalid email")
    .required("Email is required"),
  password: Yup.string()
    .required("Password is required")
    .min(8, "Password is too short")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9]).{8,}$/g,
      "Password must contain at least one uppercase and at least one lowercase letter and at least one special character"
    ),
});

/**
 * MCP OAuth bounce. When our backend redirects the browser here during an
 * MCP OAuth authorize flow, it appends `?return_to=<backend /authorize url>`.
 * After a successful login we POST the access token back to that URL as
 * `autumn_session_token` so the backend can issue the OAuth auth code.
 *
 * `reauth=1` means the backend just refused the session it was handed
 * (expired, or otherwise invalid): show the form instead of auto-returning
 * the stored token, which would only be refused again.
 */
const API_BASE_URL = (
  process.env.REACT_APP_API_URL || "https://api.autumnplatform.com"
).replace(/\/$/, "");

const SESSION_TOKEN_FIELD = "autumn_session_token";

const validateReturnTo = (returnTo: string | null): URL | null => {
  if (!returnTo) return null;
  let parsed: URL;
  try {
    parsed = new URL(returnTo);
  } catch {
    return null;
  }
  if (parsed.origin !== new URL(API_BASE_URL).origin) return null;
  return parsed;
};

/**
 * Whether a stored dashboard JWT is past its `exp` claim. Decoded, never
 * verified: the backend verifies; this only decides whether auto-returning
 * the stored token is worth a round trip. An unreadable token counts as
 * expired, so the user gets the form rather than a refusal.
 */
const isStoredTokenExpired = (token: string): boolean => {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return true;
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const { exp } = JSON.parse(atob(padded)) as { exp?: unknown };
    return typeof exp === "number" && exp * 1000 <= Date.now();
  } catch {
    return true;
  }
};

/**
 * Hands the session back to the backend's /authorize as a form POST, so the
 * token travels in the request body, never in a URL (history, logs, Referer).
 * `return_to`'s query params go along as hidden inputs: the backend reads a
 * POST's OAuth params from the body only.
 */
const postToReturnTo = (returnTo: URL, accessToken: string) => {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = `${returnTo.origin}${returnTo.pathname}`;
  form.style.display = "none";

  const addField = (name: string, value: string) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  };
  returnTo.searchParams.forEach((value, name) => {
    if (name !== SESSION_TOKEN_FIELD) addField(name, value);
  });
  addField(SESSION_TOKEN_FIELD, accessToken);

  document.body.appendChild(form);
  form.submit();
};

const LoginForm = () => {
  const navigate = useNavigate();
  const [stayLoggedIn, setStayLoggedIn] = React.useState(false);
  const { returnTo, reauthRequested } = React.useMemo(() => {
    const params = new URL(window.location.href).searchParams;
    return {
      returnTo: validateReturnTo(params.get("return_to")),
      reauthRequested: params.get("reauth") === "1",
    };
  }, []);

  useEffect(() => {
    if (
      localStorage.getItem("token") &&
      localStorage.getItem("staySignedIn") === "true" &&
      localStorage.getItem("user")
    ) {
      if (returnTo) {
        const storedToken = localStorage.getItem("token") as string;
        // A refused or expired session would only be refused again: show
        // the form so the user signs in fresh.
        if (reauthRequested || isStoredTokenExpired(storedToken)) return;
        postToReturnTo(returnTo, storedToken);
        return;
      }
      // The stored account's landing route, not a fixed /dashboard: a
      // MarketingOnly or pricing-hidden account would only be bounced there
      // by the guard anyway.
      navigate(getDefaultRouteForUser(getUser()));
    }
  }, []);

  return (
    <AuthSplitLayout>
      <Formik
        initialValues={{ email: "", password: "" }}
        isInitialValid={false}
        validationSchema={validationSchema}
        onSubmit={(values, actions) => {
              api
                .login({
                  ...values,
                })
                .then(async (res) => {
                  let auth = res.data as AuthResponse;
                  localStorage.setItem("token", auth.accessToken);
                  setToken(auth.accessToken);
                  const target = pickAutoSwitchTarget(auth);
                  if (target) {
                    try {
                      const switchRes = await api.switchProperty(target.userId);
                      auth = switchRes.data as AuthResponse;
                      localStorage.setItem("token", auth.accessToken);
                      setToken(auth.accessToken);
                    } catch (e) {
                      // Do nothing
                    }
                  }

                  localStorage.setItem("staySignedIn", `${stayLoggedIn}`);
                  if (auth.id) {
                    const userResponse = await api.getUserProfile(auth.id);
                    const userData = { ...auth, ...userResponse };
                    setUser(userData); // This normalizes the role

                    const onboardingResponse = await api.getOnboarding(
                      auth.id
                    );
                    const isOnboardingCompleted =
                      onboardingResponse?.isOnboardingCompleted;
                    actions.setSubmitting(false);
                    api.setActiveStatus(auth.id);

                    // Use RBAC system for role-based navigation
                    // Role is normalized in setUser(), so get it from stored user
                    const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
                    const userRole = storedUser.type;

                    if (!isValidUserRole(userRole)) {
                      console.error(`Invalid user role: ${userRole}`);
                      ShowToast({
                        message: "Invalid user role. Please contact support.",
                        type: "error",
                      });
                      return;
                    }

                    // MCP OAuth bounce — if a validated return_to was provided,
                    // POST the access token back to the backend /authorize and
                    // skip normal SPA routing.
                    if (returnTo) {
                      postToReturnTo(returnTo, auth.accessToken);
                      return;
                    }

                    if (isOnboardingCompleted) {
                      // Landing route for this account: the role's default,
                      // unless the backend hid the pricing dashboard.
                      const defaultRoute = getDefaultRouteForUser(storedUser);
                      navigate(defaultRoute);
                    } else {
                      navigate("/onboarding");
                    }
                  } else {
                    actions.setSubmitting(false);
                  }
                })
                .catch((err) => {
                  actions.setSubmitting(false);

                  ShowToast({
                    message:
                      err.response?.data?.message || err.message || "Invalid email or password",
                    type: "error",
                  });
                });
            }}
          >
        <LoginFormContent
          stayLoggedIn={stayLoggedIn}
          setStayLoggedIn={setStayLoggedIn}
        />
      </Formik>
    </AuthSplitLayout>
  );
};

const LoginFormContent = ({ stayLoggedIn, setStayLoggedIn }) => {
  const { isValid, isSubmitting } = useFormikContext();

  // AuthSplitLayout already paints the cream-soft form card around us
  // (radius 12, cutout on the left edge). This component only owns the
  // typography + fields, no card chrome of its own.
  return (
    <>
      <div className="flex flex-col items-center text-center gap-1.5">
        <h1
          className="text-[22px] font-semibold tracking-[-0.01em] text-[var(--d-ink)]"
          style={{ fontFamily: "var(--d-font-sans)" }}
        >
          Sign in to Autumn
        </h1>
        <p
          className="text-[13px] leading-[1.5] text-[var(--d-ink-muted)]"
          style={{ fontFamily: "var(--d-font-body)" }}
        >
          Welcome back! Please sign in to continue.
        </p>
      </div>

      <Form className="flex flex-col gap-5">
        <div className="flex flex-col gap-4">
          <FormField
            label="Email"
            name="email"
            type="email"
            placeholder="you@hotel.com"
          />
          <FormField
            label="Password"
            name="password"
            type="password"
            placeholder="••••••••"
          />
        </div>

        <div className="flex items-center justify-between gap-2 -mt-1">
          <Checkbox
            name="stay"
            id="stay"
            onChange={() => setStayLoggedIn((prev) => !prev)}
            checked={stayLoggedIn}
            label="Stay signed in"
          />
          <Link
            to="/forgot-password"
            className="text-[13px] font-medium text-[var(--d-accent-deep)] hover:text-[var(--d-accent)] transition-colors"
            style={{ fontFamily: "var(--d-font-body)" }}
          >
            Forgot password?
          </Link>
        </div>

        <Button
          className="w-full !justify-center"
          type="submit"
          disabled={!isValid}
          loading={isSubmitting}
        >
          <span className="inline-flex items-center gap-2">
            Sign in
            <RightArrow />
          </span>
        </Button>
      </Form>

      <p
        className="text-[13px] text-center text-[var(--d-ink-muted)]"
        style={{ fontFamily: "var(--d-font-body)" }}
      >
        Don't have an account?{" "}
        <Link
          to="/register"
          className="font-medium text-[var(--d-accent-deep)] hover:text-[var(--d-accent)] hover:underline transition-colors"
        >
          Sign up
        </Link>
      </p>
    </>
  );
};

export default LoginForm;
